import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { getFormTypeColour, formatDate, formatTime, resolveImageSrc, QUESTION_TYPES } from '../utils/occurrenceHelpers';
import { BsCheck2, BsX } from 'react-icons/bs';
import { useOccurrenceHistory } from '../hooks/useOccurrenceHistory';
import FollowUpForm from './FollowUpForm';

function formatAnswer(questionType, answer) {
    if (!answer && answer !== 0) return '—';

    switch (Number(questionType)) {
        case QUESTION_TYPES.DATE:
            return formatDate(answer);

        case QUESTION_TYPES.TIME:
            return formatTime(answer);

        case QUESTION_TYPES.DATE_TIME: {
            const parts = String(answer).split('T');
            return parts.length === 2
                ? `${formatDate(answer)} ${formatTime(answer)}`
                : answer;
        }

        case QUESTION_TYPES.YES_NO: {
            const normalised = String(answer).toLowerCase();
            if (normalised === 'yes' || normalised === 'true' || normalised === '1') return <><BsCheck2 color="#4CAF50" /> Yes</>;
            if (normalised === 'no' || normalised === 'false' || normalised === '0') return <><BsX color="#f44336" /> No</>;
            return answer;
        }

        case QUESTION_TYPES.TICK_BOX: {
            const checked = String(answer).toLowerCase();
            if (checked === 'yes' || checked === 'true' || checked === '1') return <><BsCheck2 color="#4CAF50" /> Yes</>;
            return <><BsX color="#f44336" /> No</>;
        }

        case QUESTION_TYPES.SIGNATURE: {
            const sigSrc = resolveImageSrc(answer);
            if (sigSrc) {
                return <img src={sigSrc} alt="Signature" className="occurrence-summary-tab__answer-thumb occurrence-summary-tab__answer-thumb--signature" />;
            }
            return 'No signature';
        }

        case QUESTION_TYPES.MAP: {
            const coords = String(answer).match(/-?\d+\.?\d*/g);
            if (coords && coords.length >= 2) {
                return (
                    <a
                        href={`https://maps.google.com/maps?q=${coords[0]},${coords[1]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="occurrence-summary-tab__map-link"
                    >
                        View on map
                    </a>
                );
            }
            return 'No location recorded';
        }

        case QUESTION_TYPES.IMAGE: {
            const imgSrc = resolveImageSrc(answer);
            if (imgSrc) {
                return <img src={imgSrc} alt="Response" className="occurrence-summary-tab__answer-thumb" />;
            }
            return 'No image';
        }

        default:
            return answer;
    }
}

function formatDateTime(isoStr) {
    if (!isoStr) return '';
    return `${formatDate(isoStr)} — ${formatTime(isoStr)}`;
}

function OccurrenceTimeline({ incidentId }) {
    const { history, loading } = useOccurrenceHistory(incidentId);

    if (loading) {
        return (
            <div className="occurrence-summary-tab__timeline-section">
                <h4 className="occurrence-summary-tab__section-title">Timeline</h4>
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if (!history || history.length === 0) return null;

    return (
        <div className="occurrence-summary-tab__timeline-section">
            <h4 className="occurrence-summary-tab__section-title">Timeline</h4>
            <div className="occurrence-timeline">
                {history.map((item, i) => (
                    <div key={i} className="occurrence-timeline__item">
                        <div className="occurrence-timeline__marker" />
                        {i < history.length - 1 && <div className="occurrence-timeline__line" />}
                        <div className="occurrence-timeline__content">
                            <span className="occurrence-timeline__date">{formatDateTime(item.dt)}</span>
                            <span className="occurrence-timeline__description">{item.description}</span>
                            <span className="occurrence-timeline__author">{item.carriedOutBy}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function OccurrenceDetails({ occurrence, colour }) {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const photoSrc = resolveImageSrc(occurrence.photoUrl) || resolveImageSrc(occurrence.photoFile);

    return (
        <>
            <div className="occurrence-summary-tab__header">
                <h3>
                    Occurrence #{occurrence.incidentID}
                    <span
                        className="occurrence-summary-tab__badge"
                        style={{ backgroundColor: colour }}
                    >
                        {occurrence.formTitle}
                    </span>
                </h3>
            </div>

            <div className="occurrence-summary-tab__detail-grid">
                <div className="occurrence-summary-tab__detail-item">
                    <span className="occurrence-summary-tab__detail-label">Date</span>
                    <span>{formatDate(occurrence.dateOfIncident)}</span>
                </div>
                <div className="occurrence-summary-tab__detail-item">
                    <span className="occurrence-summary-tab__detail-label">Site</span>
                    <span>{occurrence.siteName}</span>
                </div>
                <div className="occurrence-summary-tab__detail-item">
                    <span className="occurrence-summary-tab__detail-label">Department</span>
                    <span>{occurrence.department}</span>
                </div>
                <div className="occurrence-summary-tab__detail-item">
                    <span className="occurrence-summary-tab__detail-label">Reported By</span>
                    <span>{occurrence.staffName}</span>
                </div>
                <div className="occurrence-summary-tab__detail-item">
                    <span className="occurrence-summary-tab__detail-label">Role</span>
                    <span>{occurrence.roleName}</span>
                </div>
                <div className="occurrence-summary-tab__detail-item">
                    <span className="occurrence-summary-tab__detail-label">Shift</span>
                    <span>
                        {formatTime(occurrence.shiftStart)}
                        {(occurrence.shiftStart || occurrence.shiftFinish) && ' – '}
                        {formatTime(occurrence.shiftFinish)}
                    </span>
                </div>
            </div>

            {occurrence.lat && occurrence.lng && (
                <div className="occurrence-summary-tab__map">
                    <iframe
                        title={`Location for occurrence #${occurrence.incidentID}`}
                        src={`https://maps.google.com/maps?q=${occurrence.lat},${occurrence.lng}&z=15&output=embed`}
                        width="100%"
                        height="250"
                        style={{ border: 0, borderRadius: '6px' }}
                        loading="lazy"
                        allowFullScreen
                    />
                </div>
            )}

            {photoSrc && (
                <div className="occurrence-summary-tab__photo-section">
                    <h4 className="occurrence-summary-tab__section-title">Photo</h4>
                    <img
                        src={photoSrc}
                        alt={`Occurrence #${occurrence.incidentID}`}
                        className="occurrence-summary-tab__photo"
                        onClick={() => setLightboxOpen(true)}
                    />
                </div>
            )}

            {lightboxOpen && photoSrc && (
                <div className="occurrence-lightbox" onClick={() => setLightboxOpen(false)}>
                    <img
                        src={photoSrc}
                        alt={`Occurrence #${occurrence.incidentID}`}
                        className="occurrence-lightbox__image"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button className="occurrence-lightbox__close" onClick={() => setLightboxOpen(false)}>✕</button>
                </div>
            )}

            <OccurrenceTimeline incidentId={occurrence.incidentID} />

            {occurrence.notes && (
                <div className="occurrence-summary-tab__notes-section">
                    <h4 className="occurrence-summary-tab__section-title">Notes</h4>
                    <p className="occurrence-summary-tab__notes-text">{occurrence.notes}</p>
                </div>
            )}

            {occurrence.answers && occurrence.answers.length > 0 && (
                <div className="occurrence-summary-tab__answers">
                    <h4 className="occurrence-summary-tab__section-title">Questions &amp; Answers</h4>
                    {occurrence.answers.map((a) => (
                        <div key={a.questionID} className="occurrence-summary-tab__answer-item">
                            <span className="occurrence-summary-tab__question">{a.questionDescription}</span>
                            <span className="occurrence-summary-tab__answer">
                                {formatAnswer(a.questionType, a.answer)}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {occurrence.parentIncidentID && (
                <div className="occurrence-summary-tab__history">
                    <h4 className="occurrence-summary-tab__section-title">Parent Occurrence</h4>
                    <p>
                        Follow-up of Occurrence <strong>#{occurrence.parentIncidentID}</strong>
                        {occurrence.parentDateOfIncident && (
                            <> — {formatDate(occurrence.parentDateOfIncident)}</>
                        )}
                    </p>
                    {occurrence.parentNotes && (
                        <p className="occurrence-summary-tab__parent-notes">{occurrence.parentNotes}</p>
                    )}
                </div>
            )}
        </>
    );
}

function OccurrenceSummaryTab({
    occurrence,
    isFollowUp,
    hasAdvancedOccurrences,
    allIncidents,
    onBack,
    onFollowUpCreated,
}) {
    const colour = getFormTypeColour(occurrence.formTitle);
    const showFollowUpForm = isFollowUp && hasAdvancedOccurrences;

    if (showFollowUpForm) {
        return (
            <div className="occurrence-summary-tab">
                <button className="occurrence-summary-tab__back-btn" onClick={onBack}>
                    &larr; Back to Detail
                </button>
                <div className="occurrence-summary-tab__two-col">
                    <div className="occurrence-summary-tab__left">
                        <OccurrenceDetails occurrence={occurrence} colour={colour} />
                    </div>
                    <div className="occurrence-summary-tab__right">
                        <FollowUpForm
                            parentIncidentID={occurrence.incidentID}
                            allIncidents={allIncidents}
                            onSuccess={onFollowUpCreated}
                            onCancel={onBack}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="occurrence-summary-tab">
            <button className="occurrence-summary-tab__back-btn" onClick={onBack}>
                &larr; Back to Detail
            </button>
            <OccurrenceDetails occurrence={occurrence} colour={colour} />
            {isFollowUp && !hasAdvancedOccurrences && (
                <div className="occurrence-followup__error">
                    You do not have permission to create follow-ups.
                </div>
            )}
        </div>
    );
}

OccurrenceSummaryTab.propTypes = {
    occurrence: PropTypes.object.isRequired,
    isFollowUp: PropTypes.bool.isRequired,
    hasAdvancedOccurrences: PropTypes.bool,
    allIncidents: PropTypes.array.isRequired,
    onBack: PropTypes.func.isRequired,
    onFollowUpCreated: PropTypes.func,
};

export default OccurrenceSummaryTab;
